import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import type { CaseStudy } from '@/content/financial/types';
import { CATEGORY_LABELS, EFFORT_LABELS } from '@/content/financial/types';
import { cashOnCashRatio, monthlyProfitCents } from '@/content/financial/case-studies';
import { formatCentsCompact, formatRatioAsPercent } from '@/content/financial/format';
import SourceBadge from './SourceBadge';

/**
 * Case study card.
 *
 * Leads with three figures rather than a paragraph, because the figures are
 * why anyone opens one of these. Cash-on-cash is deliberately allowed to be
 * "—": `cashOnCashRatio` returns null when the capital base is too small for
 * the ratio to mean anything, and printing a 1,300% return would be worse than
 * printing nothing.
 */
export default function CaseStudyCard({
  study,
  featured = false,
  className,
}: {
  study: CaseStudy;
  featured?: boolean;
  className?: string;
}) {
  const category = CATEGORY_LABELS[study.category];
  const coc = cashOnCashRatio(study);

  return (
    <Link
      to={`/financial/case-studies/${study.slug}`}
      className={twMerge(
        'group flex flex-col overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm',
        'transition-all duration-200 hover:-translate-y-1 hover:shadow-md',
        'dark:border-surface-dark-border dark:bg-surface-dark-card',
        featured && 'sm:flex-row',
        className
      )}
    >
      {/* Cover */}
      <div
        className={twMerge(
          'relative flex flex-shrink-0 items-end bg-gradient-to-br p-4',
          study.gradient,
          featured ? 'min-h-[160px] sm:w-2/5' : 'h-[120px]'
        )}
      >
        <span lang="ko" className="font-korean text-sm font-medium text-white/70" aria-hidden="true">
          {category.labelKo}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-cheong-500 dark:text-cheong-dark">
            {category.label}
          </span>
          <SourceBadge sourceType={study.sourceType} />
        </div>

        <h3
          className={twMerge(
            'font-bold leading-snug text-ink-900 dark:text-ink-50',
            featured ? 'text-xl' : 'text-base'
          )}
        >
          {study.title}
        </h3>

        <p className="mt-2 flex-1 text-sm leading-6 text-ink-500 line-clamp-3 dark:text-ink-400">
          {study.dek}
        </p>

        {/* Economics strip */}
        <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-ink-200 pt-3 dark:border-surface-dark-border">
          <Figure label="Cash in" value={formatCentsCompact(study.cashInvestedCents ?? study.startupCostCents)} />
          <Figure label="Monthly profit" value={formatCentsCompact(monthlyProfitCents(study))} />
          <Figure label="Cash-on-cash" value={coc == null ? '—' : formatRatioAsPercent(coc, 0)} />
        </dl>

        <div className="mt-4 flex items-center justify-between text-xs text-ink-400">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3 w-3" aria-hidden="true" />
            {study.readingMinutes} min · {EFFORT_LABELS[study.effort]}
            {study.hoursPerWeek != null && ` · ${study.hoursPerWeek} hrs/wk`}
          </span>
          <span className="inline-flex items-center gap-1 font-medium text-cheong-500 group-hover:gap-2 dark:text-cheong-dark [transition:gap_150ms_ease-out]">
            Read
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wide text-ink-400">{label}</dt>
      <dd className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-ink-900 dark:text-ink-50">
        {value}
      </dd>
    </div>
  );
}
